import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CalendarModule } from 'primeng/calendar';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService, PrimeNGConfig } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { PermissionService } from '../../../core/services/permission.service';
import { UnsavedChangesService } from '../../../core/services/unsaved-changes.service';

import { WorkingGroupService } from '../../../core/services/working-group.service';
import { WorkingGroup } from '../../../core/models/working-group';
import { ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { DailyWorkingHourService } from '../../../core/services/daily-working-hour.service';
import { DailyWorkingHour } from '../../../core/models/daily-working-hour';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

interface WorklogTableRow {
  group: WorkingGroup;
  period: any;
}

@Component({
  selector: 'em-working-hours',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    DropdownModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    CalendarModule,
    ToastModule,
    ConfirmDialogModule,
    TranslateModule,
    EmPermissionDirective,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './working-hours.component.html',
  styleUrls: ['./working-hours.component.scss'],
})
export class WorkingHoursComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table!: Table;
  Permission = Permission;
  canWrite = false;
  private readonly destroy$ = new Subject<void>();
  private readonly fallbackDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  private readonly fallbackDayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  private readonly fallbackDayNamesMin = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  private readonly fallbackMonthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  private readonly fallbackMonthNamesShort = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  private readonly MAX_HOURS_PER_WEEK = 60;
  private readonly MAX_HOURS_PER_DAY = 12;

  currentYear = new Date().getFullYear();
  years: { label: string; value: number }[] = [];
  selectedYear!: number;
  periodMinDate!: Date;
  periodMaxDate!: Date;
  allWorkingGroups: WorkingGroup[] = [];
  workingGroups: WorkingGroup[] = [];
  worklogRows: WorklogTableRow[] = [];
  hasPendingChanges = false;
  isSavingPendingChanges = false;
  private tempGroupId = -1;
  private tempPeriodId = -1;
  private pendingGroupIds = new Set<number>();
  private draftDailySchedules: Record<number, DailyWorkingHour[]> = {};
  private manualBreakOverrides = new WeakSet<object>();
  private dbWorkingGroupsSnapshot: WorkingGroup[] = [];

  displayDialog = false;
  addGroupDirty = false;

  newGroup: WorkingGroup = {
    groupName: '',
    year: '',
    numberOfPeriods: 2,
  };

  normalHours: number = 0;
  summerHours: number = 0;
  ramadanHours: number = 0;
  yearPartitions: any[] = [];

  displayPeriodDialog = false;
  periodDirty = false;

  newPeriod: any = {
    id: null,
    type: '',
    hoursPerWeek: 0,
    startDate: new Date(),
    endDate: new Date(),
  };
  private previousPeriod: any = null;
  private nextPeriod: any = null;
  private originalPeriodDates: { startDate: Date; endDate: Date } | null = null;
  private editingGroupId: number | null = null;
  private isEditingDraftPeriod = false;
  isEditingFirstPeriod = false;
  isEditingLastPeriod = false;
  displayGroupReassignDialog = false;
  groupPendingDeletionId: number | null = null;
  groupPendingDeletionName = '';
  reassignTargetGroupId: number | null = null;
  reassignMode: 'existing' | 'create' = 'existing';
  newReassignGroupName = '';
  isCreatingReassignGroup = false;
  private groupDeletionTargetByGroupId: Record<number, number> = {};

  constructor(
    private workingGroupService: WorkingGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dailyService: DailyWorkingHourService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private primengConfig: PrimeNGConfig,
    private unsavedChangesService: UnsavedChangesService,
  ) {}

  ngOnInit() {
    this.setPermissionFlags();
    this.permissionService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => this.setPermissionFlags());
    this.applyCalendarTranslations();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyCalendarTranslations());
    this.unsavedChangesService.register('working-hours', () => this.hasUnsavedChanges());

    for (let i = 0; i <= 10; i++) {
      const year = this.currentYear + i;
      this.years.push({ label: year.toString(), value: year });
    }

    this.selectedYear = this.currentYear;
    this.updatePeriodDateBounds();
    this.loadData();
  }

  private applyCalendarTranslations(): void {
    const translations = this.translate.instant('calendar');
    const isValidArray = (value: unknown, expectedLength: number): value is string[] =>
      Array.isArray(value) &&
      value.length === expectedLength &&
      value.every((item) => typeof item === 'string' && item.trim().length > 0);

    this.primengConfig.setTranslation({
      dayNames: isValidArray(translations?.dayNames, 7) ? translations.dayNames : this.fallbackDayNames,
      dayNamesShort: isValidArray(translations?.dayNamesShort, 7)
        ? translations.dayNamesShort
        : this.fallbackDayNamesShort,
      dayNamesMin: isValidArray(translations?.dayNamesMin, 7) ? translations.dayNamesMin : this.fallbackDayNamesMin,
      monthNames: isValidArray(translations?.monthNames, 12) ? translations.monthNames : this.fallbackMonthNames,
      monthNamesShort: isValidArray(translations?.monthNamesShort, 12)
        ? translations.monthNamesShort
        : this.fallbackMonthNamesShort,
      today:
        typeof translations?.today === 'string' && translations.today.trim().length > 0 ? translations.today : 'Today',
      clear:
        typeof translations?.clear === 'string' && translations.clear.trim().length > 0 ? translations.clear : 'Clear',
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  hasUnsavedChanges(): boolean {
    return this.hasPendingChanges || this.addGroupDirty || this.periodDirty || this.scheduleDirty;
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges()) {
      return true;
    }
    return new Promise<boolean>((resolve) => {
      this.confirmationService.confirm({
        message: this.translate.instant('worklog.unsavedChangesMessage'),
        header: this.translate.instant('worklog.unsavedChangesHeader'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('worklog.leave'),
        rejectLabel: this.translate.instant('worklog.stay'),
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregister('working-hours');
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAddGroupFieldChange(): void {
    this.addGroupDirty = true;
  }

  onPeriodFieldChange(): void {
    this.periodDirty = true;
  }

  private setPermissionFlags(): void {
    this.canWrite = this.permissionService.hasPermission(Permission.CONFIGURATION_WRITE);
  }

  private getErrorSummary(): string {
    const translated = this.translate.instant('common.error.title');
    return typeof translated === 'string' && translated.trim().length ? translated : 'Error';
  }

  private resolveBackendErrorDetail(error: any): string {
    const fieldErrorMessage = error?.error?.fieldErrors?.[0]?.errorMessage;
    if (typeof fieldErrorMessage === 'string' && fieldErrorMessage.trim().length) {
      return fieldErrorMessage;
    }

    const message = error?.error?.message || error?.message;
    if (typeof message === 'string' && message.trim().length) {
      return message;
    }

    return this.translate.instant('worklog.saveError');
  }

  private isWeeklyHoursValid(hours: number): boolean {
    return Number.isFinite(hours) && hours >= 0 && hours <= this.MAX_HOURS_PER_WEEK;
  }

  private updatePeriodDateBounds() {
    this.periodMinDate = new Date(this.selectedYear, 0, 1);
    this.periodMaxDate = new Date(this.selectedYear, 11, 31);
    this.periodMinDate.setHours(0, 0, 0, 0);
    this.periodMaxDate.setHours(0, 0, 0, 0);
  }

  private periodOverlapsSelectedYear(period: any): boolean {
    const periodStart = this.normalizeNullableDate(period?.startDate);
    const periodEnd = this.normalizeNullableDate(period?.endDate) ?? periodStart;
    if (!periodStart || !periodEnd) {
      return false;
    }
    return periodEnd.getTime() >= this.periodMinDate.getTime() && periodStart.getTime() <= this.periodMaxDate.getTime();
  }

  private isDateInSelectedYear(date: Date | string | null | undefined): boolean {
    const normalized = this.normalizeNullableDate(date);
    if (!normalized) {
      return false;
    }
    return normalized.getTime() >= this.periodMinDate.getTime() && normalized.getTime() <= this.periodMaxDate.getTime();
  }

  private findEmptyGroupForYear(groupName: string): WorkingGroup | undefined {
    const normalizedName = groupName.trim().toLowerCase();
    return this.allWorkingGroups.find(
      (group) =>
        group.groupName?.trim().toLowerCase() === normalizedName &&
        group.year === this.selectedYear.toString() &&
        (!group.periods || group.periods.length === 0),
    );
  }

  private createGroup() {
    this.workingGroupService.create(this.newGroup).subscribe({
      next: () => {
        this.displayDialog = false;
        this.loadData();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('worklog.summaries.created'),
          detail: this.translate.instant('worklog.groupCreated'),
        });
      },
      error: (err) => {
        let errorMessage = this.translate.instant('worklog.creationFailed');

      const backendMessage: string = err?.error?.message || (typeof err.error === 'string' ? err.error : '');
      if (backendMessage) {
        errorMessage = /already exists/i.test(backendMessage)
          ? this.translate.instant('worklog.groupAlreadyExists')
          : backendMessage;
      }

      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: errorMessage
      });
    }
      });
  }

  loadData() {
    this.workingGroupService.getByYear(this.selectedYear.toString()).subscribe({
      next: (data) => {
        this.allWorkingGroups = data;
        this.dbWorkingGroupsSnapshot = this.cloneGroups(
          data.map((group) => ({
            ...group,
            periods: (group.periods || [])
              .filter((period) => this.periodOverlapsSelectedYear(period))
              .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
          })),
        );
        this.workingGroups = this.cloneGroups(this.dbWorkingGroupsSnapshot);

        this.rebuildWorklogRows();
        this.pendingGroupIds.clear();
        this.draftDailySchedules = {};
        this.groupDeletionTargetByGroupId = {};
        this.displayGroupReassignDialog = false;
        this.groupPendingDeletionId = null;
        this.groupPendingDeletionName = '';
        this.reassignTargetGroupId = null;
        this.reassignMode = 'existing';
        this.newReassignGroupName = '';
        this.isCreatingReassignGroup = false;
        this.hasPendingChanges = false;
        this.tempGroupId = -1;
        this.tempPeriodId = -1;
      },
    });
  }
  onYearChange() {
    if (this.hasPendingChanges) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('worklog.summaries.unsaved'),
        detail: this.translate.instant('worklog.unsavedChangesDetail'),
      });
      return;
    }
    this.updatePeriodDateBounds();
    this.loadData();
  }

  openAddDialog() {
    this.newGroup = {
      groupName: '',
      year: this.selectedYear.toString(),
      numberOfPeriods: 2,
    };
    this.normalHours = 0;
    this.summerHours = 0;
    this.ramadanHours = 0;
    this.addGroupDirty = false;
    this.displayDialog = true;
  }

  onAddGroupDialogHide() {
    // no-op: dialog hide is handled by displayDialog binding
  }

  cancelAddGroup() {
    this.displayDialog = false;
  }

  saveGroup() {
    this.newGroup.year = this.selectedYear.toString();

    this.newGroup.normalHours = this.normalHours;
    this.newGroup.summerHours = this.summerHours;
    this.newGroup.ramadanHours = this.ramadanHours;

  if (![this.normalHours, this.summerHours, this.ramadanHours].every((h) => this.isWeeklyHoursValid(h))) {
    this.messageService.add({
      severity: 'error',
      summary: this.getErrorSummary(),
      detail: this.translate.instant('worklog.hoursExceedWeeklyMax')
    });
    return;
  }

    if (!this.validateWeeklyHours(this.normalHours, this.translate.instant('worklog.hourTypes.normal'))) return;
    if (!this.validateWeeklyHours(this.summerHours, this.translate.instant('worklog.hourTypes.summer'))) return;
    if (!this.validateWeeklyHours(this.ramadanHours, this.translate.instant('worklog.hourTypes.ramadan'))) return;


    if (this.newGroup.id) {

    this.workingGroupService
      .update(this.newGroup.id, this.newGroup)
      .subscribe({
        next: () => {
          this.addGroupDirty = false;
          this.displayDialog = false;
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.success.title'),
            detail: this.translate.instant('worklog.groupUpdated'),
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: this.getErrorSummary(),
            detail: this.translate.instant('worklog.groupUpdateFailed'),
          });
        },
      });

  } else {
    const groupName = this.newGroup.groupName?.trim();
    if (!groupName) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.groupNameRequired')
      });
      return;
    }

    const normalizedName = groupName.toLowerCase();
    const isDuplicate = this.workingGroups.some((group) =>
      group.year === this.selectedYear.toString() &&
      group.groupName?.trim().toLowerCase() === normalizedName
    );

    if (isDuplicate) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.groupAlreadyExists')
      });
      return;
    }

      const draftGroup: WorkingGroup = {
        id: this.tempGroupId--,
        groupName,
        year: this.selectedYear.toString(),
        numberOfPeriods: this.newGroup.numberOfPeriods || 2,
        normalHours: this.normalHours,
        summerHours: this.summerHours,
        ramadanHours: this.ramadanHours,
        periods: this.buildDraftPeriods(this.newGroup.numberOfPeriods || 2),
      };

      this.workingGroups = [...this.workingGroups, draftGroup];
      this.pendingGroupIds.add(draftGroup.id as number);
      this.rebuildWorklogRows();
      this.hasPendingChanges = true;
      this.addGroupDirty = false;
      this.displayDialog = false;
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('worklog.summaries.draft'),
        detail: this.translate.instant('worklog.draftAdded'),
      });
    }
  }
  editPeriod(group: WorkingGroup, period: any) {
    this.editingGroupId = group.id ?? null;
    this.isEditingDraftPeriod = (group.id || 0) < 0 || (period.id || 0) < 0;

    const sortedPeriods = [...(group.periods || [])].sort(
      (a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
    const currentIndex = sortedPeriods.findIndex((p: any) => p.id === period.id);
    this.previousPeriod = currentIndex > 0 ? sortedPeriods[currentIndex - 1] : null;
    this.nextPeriod =
      currentIndex >= 0 && currentIndex < sortedPeriods.length - 1 ? sortedPeriods[currentIndex + 1] : null;
    this.isEditingFirstPeriod = !this.previousPeriod;
    this.isEditingLastPeriod = !this.nextPeriod;

    const startDate = this.normalizeDate(period.startDate);
    const endDate = this.normalizeDate(period.endDate);
    this.originalPeriodDates = {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
    };

    this.newPeriod = {
      id: period.id,
      type: period.type,
      hoursPerWeek: period.hoursPerWeek,
      startDate,
      endDate,
    };

    this.periodDirty = false;
    this.displayPeriodDialog = true;
  }

  private normalizeDate(value: Date | string): Date {
    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(value);
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  private normalizeNullableDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    return this.normalizeDate(value);
  }

  private shiftDate(value: Date | string, days: number): Date {
    const shifted = this.normalizeDate(value);
    shifted.setDate(shifted.getDate() + days);
    return shifted;
  }

  private datesAreSame(left: Date | string | null | undefined, right: Date | string | null | undefined): boolean {
    const normalizedLeft = this.normalizeNullableDate(left);
    const normalizedRight = this.normalizeNullableDate(right);

    if (!normalizedLeft && !normalizedRight) {
      return true;
    }
    if (!normalizedLeft || !normalizedRight) {
      return false;
    }

    return normalizedLeft.getTime() === normalizedRight.getTime();
  }

  private rangesOverlap(
    startDateA: Date,
    endDateA: Date,
    startDateB: Date | string | null | undefined,
    endDateB: Date | string | null | undefined,
  ): boolean {
    const normalizedStartB = this.normalizeNullableDate(startDateB);
    const normalizedEndB = this.normalizeNullableDate(endDateB);
    if (!normalizedStartB || !normalizedEndB) {
      return false;
    }
    return startDateA.getTime() <= normalizedEndB.getTime() && endDateA.getTime() >= normalizedStartB.getTime();
  }

  private hasNonAdjacentOverlapInGroup(
    groupId: number | null,
    currentPeriodId: number,
    candidateStartDate: Date,
    candidateEndDate: Date,
  ): boolean {
    const group = this.workingGroups.find((item) => item.id === groupId);
    if (!group?.periods?.length) {
      return false;
    }

    const sortedPeriods = [...group.periods]
      .map((period: any) => ({
        ...period,
        startDate: this.normalizeDate(period.startDate),
        endDate: this.normalizeDate(period.endDate),
      }))
      .sort((a: any, b: any) => a.startDate.getTime() - b.startDate.getTime());

    const currentIndex = sortedPeriods.findIndex((period: any) => period.id === currentPeriodId);
    if (currentIndex < 0) {
      return false;
    }

    const adjacentIds = new Set<number>();
    if (currentIndex > 0) {
      adjacentIds.add(sortedPeriods[currentIndex - 1].id);
    }
    if (currentIndex < sortedPeriods.length - 1) {
      adjacentIds.add(sortedPeriods[currentIndex + 1].id);
    }

    return sortedPeriods.some((period: any) => {
      if (period.id === currentPeriodId || adjacentIds.has(period.id)) {
        return false;
      }
      return this.rangesOverlap(candidateStartDate, candidateEndDate, period.startDate, period.endDate);
    });
  }

  private buildProjectedPeriodsForEdit(
    effectiveStartDate: Date,
    effectiveEndDate: Date,
  ): { projectedPeriods: any[]; currentPeriodId: number; updatedHours: number } | null {
    const draftGroup = this.workingGroups.find((g) => g.id === this.editingGroupId);
    if (!draftGroup || !draftGroup.periods?.length) {
      return null;
    }

    const projectedPeriods = [...draftGroup.periods]
      .map((period: any) => ({
        ...period,
        startDate: this.normalizeDate(period.startDate),
        endDate: this.normalizeDate(period.endDate),
      }))
      .sort((a: any, b: any) => a.startDate.getTime() - b.startDate.getTime());

    const currentIndex = projectedPeriods.findIndex((period: any) => period.id === this.newPeriod.id);
    if (currentIndex < 0) {
      return null;
    }

    const currentPeriod = projectedPeriods[currentIndex];
    const previousPeriod = currentIndex > 0 ? projectedPeriods[currentIndex - 1] : null;
    const nextPeriod = currentIndex < projectedPeriods.length - 1 ? projectedPeriods[currentIndex + 1] : null;

    currentPeriod.startDate = new Date(effectiveStartDate);
    currentPeriod.endDate = new Date(effectiveEndDate);
    currentPeriod.hoursPerWeek = Number(this.newPeriod.hoursPerWeek) || 0;

    if (previousPeriod) {
      previousPeriod.endDate = this.shiftDate(effectiveStartDate, -1);
    }
    if (nextPeriod) {
      nextPeriod.startDate = this.shiftDate(effectiveEndDate, 1);
    }

    return {
      projectedPeriods,
      currentPeriodId: currentPeriod.id,
      updatedHours: currentPeriod.hoursPerWeek,
    };
  }

  private validateProjectedPeriods(projectedPeriods: any[]): { valid: boolean; messageKey?: string } {
    if (!projectedPeriods.length) {
      return { valid: false, messageKey: 'worklog.continuityRequired' };
    }

    const sorted = [...projectedPeriods].sort(
      (a: any, b: any) => this.normalizeDate(a.startDate).getTime() - this.normalizeDate(b.startDate).getTime(),
    );

    let expectedStart = new Date(this.periodMinDate);

    for (const period of sorted) {
      const startDate = this.normalizeDate(period.startDate);
      const endDate = this.normalizeDate(period.endDate);

      if (startDate.getTime() > endDate.getTime()) {
        return { valid: false, messageKey: 'worklog.startBeforeEnd' };
      }

      if (startDate.getTime() !== expectedStart.getTime()) {
        return { valid: false, messageKey: 'worklog.continuityRequired' };
      }

      expectedStart = this.shiftDate(endDate, 1);
    }

    const finalExpected = this.shiftDate(this.periodMaxDate, 1);
    if (expectedStart.getTime() !== finalExpected.getTime()) {
      return { valid: false, messageKey: 'worklog.continuityRequired' };
    }

    return { valid: true };
  }

  private validateWeeklyHours(hours: number | undefined | null, label: string): boolean {
    if (hours === null || hours === undefined) return true;
    if (hours < 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.weeklyHoursNegative', { label }),
      });
      return false;
    }
    if (hours > 120) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.weeklyHoursExceedMax', { label, max: 120 }),
      });
      return false;
    }
    return true;
  }

  private formatDateForApi(value: Date | null): string | null {
    if (!value) {
      return null;
    }

    const year = value.getFullYear();
    const month = (value.getMonth() + 1).toString().padStart(2, '0');
    const day = value.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildPeriodPayload(period: any, overrides?: { startDate?: Date | null; endDate?: Date | null }): any {
    const startDate = overrides?.startDate ?? this.normalizeNullableDate(period.startDate);
    const endDate = overrides?.endDate ?? this.normalizeNullableDate(period.endDate);

    return {
      id: period.id,
      type: period.type,
      hoursPerWeek: period.hoursPerWeek,
      startDate: this.formatDateForApi(startDate),
      endDate: this.formatDateForApi(endDate),
    };
  }

  onPeriodDialogHide(): void {
    if (this.periodDirty) {
      this.confirmationService.confirm({
        message: this.translate.instant('worklog.unsavedChangesMessage'),
        header: this.translate.instant('worklog.unsavedChangesHeader'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('worklog.leave'),
        rejectLabel: this.translate.instant('worklog.stay'),
        accept: () => {
          this.displayPeriodDialog = false;
          this.resetPeriodEditContext();
        },
        reject: () => {
          setTimeout(() => {
            this.displayPeriodDialog = true;
          });
        },
      });
    } else {
      this.resetPeriodEditContext();
    }
  }

  cancelEditPeriod(): void {
    if (this.periodDirty) {
      this.confirmationService.confirm({
        message: this.translate.instant('worklog.unsavedChangesMessage'),
        header: this.translate.instant('worklog.unsavedChangesHeader'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('worklog.leave'),
        rejectLabel: this.translate.instant('worklog.stay'),
        accept: () => {
          this.displayPeriodDialog = false;
          this.resetPeriodEditContext();
        },
        reject: () => {
          setTimeout(() => {
            this.displayPeriodDialog = true;
          });
        },
      });
    } else {
      this.displayPeriodDialog = false;
      this.resetPeriodEditContext();
    }
  }

  resetPeriodEditContext() {
    this.previousPeriod = null;
    this.nextPeriod = null;
    this.originalPeriodDates = null;
    this.editingGroupId = null;
    this.isEditingDraftPeriod = false;
    this.isEditingFirstPeriod = false;
    this.isEditingLastPeriod = false;
  }

  updatePeriod() {
    const currentStartDate = this.normalizeNullableDate(this.newPeriod.startDate);
    const currentEndDate = this.normalizeNullableDate(this.newPeriod.endDate);

    if (!currentStartDate || !currentEndDate) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.datesRequired'),
      });
      return;
    }

    if (!this.isWeeklyHoursValid(Number(this.newPeriod.hoursPerWeek))) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.hoursExceedWeeklyMax')
      });
      return;
    }

    const effectiveStartDate = this.isEditingFirstPeriod
      ? new Date(this.periodMinDate)
      : new Date(currentStartDate);
    const effectiveEndDate = this.isEditingLastPeriod
      ? new Date(this.periodMaxDate)
      : new Date(currentEndDate);

    if (effectiveStartDate.getTime() > effectiveEndDate.getTime()) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.startBeforeEnd'),
      });
      return;
    }

    if (!this.isDateInSelectedYear(effectiveStartDate) || !this.isDateInSelectedYear(effectiveEndDate)) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.datesOutsideYear', { year: this.selectedYear }),
      });
      return;
    }

    if (
      this.hasNonAdjacentOverlapInGroup(this.editingGroupId, this.newPeriod.id, effectiveStartDate, effectiveEndDate)
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.dateAlreadyAssigned'),
      });
      return;
    }

    const projection = this.buildProjectedPeriodsForEdit(effectiveStartDate, effectiveEndDate);
    if (!projection) {
      return;
    }

    const validation = this.validateProjectedPeriods(projection.projectedPeriods);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant(validation.messageKey || 'worklog.continuityRequired'),
      });
      return;
    }

    const applied = this.applyDraftPeriodUpdate(
      projection.projectedPeriods,
      projection.currentPeriodId,
      projection.updatedHours,
    );
    if (!applied) {
      return;
    }

    this.periodDirty = false;
    this.displayPeriodDialog = false;
    this.resetPeriodEditContext();
    this.rebuildWorklogRows();
    this.hasPendingChanges = true;
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('worklog.summaries.updated'),
      detail: this.translate.instant('worklog.periodUpdatedLocal'),
    });
  }

  private applyDraftPeriodUpdate(projectedPeriods: any[], currentPeriodId: number, updatedHours: number): boolean {
    const draftGroup = this.workingGroups.find((g) => g.id === this.editingGroupId);
    if (!draftGroup || !draftGroup.periods?.length) {
      return false;
    }

    const currentPeriod = draftGroup.periods.find((period: any) => period.id === currentPeriodId);
    if (!currentPeriod) {
      return false;
    }
    const previousHoursPerWeek = Number(currentPeriod.hoursPerWeek) || 0;

    if (previousHoursPerWeek !== Number(updatedHours) || currentPeriod.id < 0) {
      this.syncDraftScheduleWithPeriodHours(currentPeriod.id, Number(updatedHours) || 0);
    }

    draftGroup.periods = projectedPeriods;
    return true;
  }

  private syncDraftScheduleWithPeriodHours(periodId: number, hoursPerWeek: number): void {
    let schedule = this.draftDailySchedules[periodId];
    if (!schedule?.length) {
      const periodType = this.findPeriodById(periodId)?.type ?? null;
      schedule = this.buildDefaultDraftSchedule(Number(hoursPerWeek) || 0, periodType);
    }

    const targets = this.getWeekdayTargetHours(Number(hoursPerWeek) || 0);
    if (targets.length !== 5) {
      return;
    }

    const weekdayOrder = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
    const weekend = ['SATURDAY', 'SUNDAY'];

    weekdayOrder.forEach((dayName, index) => {
      const row = schedule.find((day) => day.day === dayName);
      if (!row) {
        return;
      }
      row.workingHours = targets[index];
      this.recalculateDay(row);
    });

    weekend.forEach((dayName) => {
      const row = schedule.find((day) => day.day === dayName);
      if (!row) {
        return;
      }
      row.workingHours = 0;
      row.startTime = '';
      row.endTime = '';
    });

    this.draftDailySchedules[periodId] = this.cloneSchedule(schedule);
  }

  deletePeriod(id: number, group: WorkingGroup) {
    this.confirmationService.confirm({
      message: this.translate.instant('worklog.confirmDeletePeriodMessage'),
      header: this.translate.instant('worklog.confirmDeletePeriodHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('worklog.delete'),
      rejectLabel: this.translate.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.executeDeletePeriod(id, group);
      },
    });
  }

  private executeDeletePeriod(id: number, group: WorkingGroup) {
    const targetGroup = this.workingGroups.find((item) => item.id === group.id);
    if (!targetGroup?.periods?.length) {
      return;
    }

    const sortedPeriods = [...targetGroup.periods].sort(
      (a: any, b: any) => this.normalizeDate(a.startDate).getTime() - this.normalizeDate(b.startDate).getTime()
    );
    const deletedIndex = sortedPeriods.findIndex((period: any) => period.id === id);
    if (deletedIndex < 0) {
      return;
    }

    const deletedPeriod = sortedPeriods[deletedIndex];
    const previousPeriod = deletedIndex > 0 ? sortedPeriods[deletedIndex - 1] : null;
    const nextPeriod = deletedIndex < sortedPeriods.length - 1 ? sortedPeriods[deletedIndex + 1] : null;

    if (previousPeriod) {
      previousPeriod.endDate = this.normalizeDate(deletedPeriod.endDate);
    } else if (nextPeriod) {
      nextPeriod.startDate = this.normalizeDate(deletedPeriod.startDate);
    }

    targetGroup.periods = sortedPeriods.filter((period: any) => period.id !== id);
    delete this.draftDailySchedules[id];

    if (!targetGroup.periods.length) {
      this.removeLocalGroup(targetGroup);
      return;
    }

    this.rebuildWorklogRows();
    this.hasPendingChanges = true;
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('worklog.summaries.deleted'),
      detail: this.translate.instant('worklog.periodDeletedLocal'),
    });
  }

  private removeLocalGroup(group: WorkingGroup) {
    const groupId = group.id as number;
    (group.periods || []).forEach((period: any) => {
      delete this.draftDailySchedules[period.id];
    });
    this.workingGroups = this.workingGroups.filter((item) => item.id !== groupId);
    this.pendingGroupIds.delete(groupId);
    delete this.groupDeletionTargetByGroupId[groupId];
    this.hasPendingChanges = true;
    this.rebuildWorklogRows();
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('worklog.summaries.deleted'),
      detail: this.translate.instant('worklog.groupDeletedLocal'),
    });
  }

  private buildDraftPeriods(numberOfPeriods: number): any[] {
    const template = this.getTemplatePeriods(numberOfPeriods);
    return template.map((period) => ({
      id: this.tempPeriodId--,
      type: period.type,
      hoursPerWeek:
        period.type === 'SUMMER' ? this.summerHours : period.type === 'RAMADAN' ? this.ramadanHours : this.normalHours,
      startDate: new Date(period.startDate),
      endDate: new Date(period.endDate),
    }));
  }

private getTemplatePeriods(numberOfPeriods: number): { type: string; startDate: Date; endDate: Date }[] {
  return this.getFallbackTemplates(numberOfPeriods);
}

  private getFallbackTemplates(numberOfPeriods: number): { type: string; startDate: Date; endDate: Date }[] {
    if (numberOfPeriods === 1) {
      return [
        {
          type: 'NORMAL',
          startDate: new Date(this.selectedYear, 0, 1),
          endDate: new Date(this.selectedYear, 11, 31),
        },
      ];
    }

    if (numberOfPeriods === 2) {
      return [
        { type: 'NORMAL', startDate: new Date(this.selectedYear, 0, 1), endDate: new Date(this.selectedYear, 5, 30) },
        { type: 'SUMMER', startDate: new Date(this.selectedYear, 6, 1), endDate: new Date(this.selectedYear, 7, 31) },
        { type: 'NORMAL', startDate: new Date(this.selectedYear, 8, 1), endDate: new Date(this.selectedYear, 11, 31) },
      ];
    }

    const ramadan = this.getRamadanDatesForYear(this.selectedYear);
    const ramadanStart = ramadan?.startDate ?? new Date(this.selectedYear, 2, 1);
    const ramadanEnd = ramadan?.endDate ?? new Date(this.selectedYear, 2, 31);

    return [
      { type: 'NORMAL', startDate: new Date(this.selectedYear, 0, 1), endDate: this.shiftDate(ramadanStart, -1) },
      { type: 'RAMADAN', startDate: ramadanStart, endDate: ramadanEnd },
      { type: 'NORMAL', startDate: this.shiftDate(ramadanEnd, 1), endDate: new Date(this.selectedYear, 5, 30) },
      { type: 'SUMMER', startDate: new Date(this.selectedYear, 6, 1), endDate: new Date(this.selectedYear, 7, 31) },
      { type: 'NORMAL', startDate: new Date(this.selectedYear, 8, 1), endDate: new Date(this.selectedYear, 11, 31) },
    ];
  }

  private getRamadanDatesForYear(year: number): { startDate: Date; endDate: Date } | null {
    switch (year) {
      case 2026:
        return {
          startDate: new Date(2026, 1, 19),
          endDate: new Date(2026, 2, 20),
        };
      case 2027:
        return {
          startDate: new Date(2027, 1, 8),
          endDate: new Date(2027, 2, 9),
        };
      default:
        return null;
    }
  }

  private cloneGroups(groups: WorkingGroup[]): WorkingGroup[] {
    return groups.map((group) => ({
      ...group,
      periods: (group.periods || []).map((period: any) => ({ ...period })),
    }));
  }

  private rebuildWorklogRows() {
    this.worklogRows = this.workingGroups.flatMap((group) =>
      (group.periods || []).map((period: any) => ({ group, period })),
    );

    this.groupOptions = this.workingGroups.map((g) => ({
      label: g.groupName,
      value: g.id,
    }));
  }

  isDraftRow(row: WorklogTableRow): boolean {
    return (row.group.id || 0) < 0 || (row.period.id || 0) < 0;
  }

  private extractHoursForType(periods: any[], type: string): number {
    const item = periods.find((p) => String(p.type || '').toUpperCase() === type);
    return item ? Number(item.hoursPerWeek) || 0 : 0;
  }

  private periodHasChanges(originalPeriod: any, currentPeriod: any): boolean {
    return (
      String(originalPeriod?.type || '').toUpperCase() !== String(currentPeriod?.type || '').toUpperCase() ||
      (Number(originalPeriod?.hoursPerWeek) || 0) !== (Number(currentPeriod?.hoursPerWeek) || 0) ||
      !this.datesAreSame(originalPeriod?.startDate, currentPeriod?.startDate) ||
      !this.datesAreSame(originalPeriod?.endDate, currentPeriod?.endDate)
    );
  }

  get reassignTargetOptions(): { label: string; value: number }[] {
    if (this.groupPendingDeletionId == null) {
      return [];
    }

    return this.workingGroups
      .filter(
        (group) =>
          (group.id || 0) > 0 &&
          group.id !== this.groupPendingDeletionId &&
          group.year === this.selectedYear.toString(),
      )
      .map((group) => ({
        label: group.groupName,
        value: group.id as number,
      }));
  }

  private requiresGroupReassignment(error: any): boolean {
    const rawMessage = String(error?.error?.message || error?.message || '').toLowerCase();
    return (
      rawMessage.includes('target working group') ||
      rawMessage.includes('assigned to users') ||
      rawMessage.includes('reassign')
    );
  }

  private openGroupReassignDialog(groupId: number, groupName: string): boolean {
    this.groupPendingDeletionId = groupId;
    this.groupPendingDeletionName = groupName;
    this.reassignTargetGroupId = this.groupDeletionTargetByGroupId[groupId] ?? null;
    this.reassignMode = this.reassignTargetOptions.length ? 'existing' : 'create';
    this.newReassignGroupName = '';
    this.isCreatingReassignGroup = false;

    this.displayGroupReassignDialog = true;
    return true;
  }

  private getPendingDeletionSourceGroup(): WorkingGroup | null {
    if (this.groupPendingDeletionId == null) {
      return null;
    }

    const snapshotGroup = this.dbWorkingGroupsSnapshot.find((group) => group.id === this.groupPendingDeletionId);
    if (snapshotGroup) {
      return snapshotGroup;
    }

    return this.allWorkingGroups.find((group) => group.id === this.groupPendingDeletionId) ?? null;
  }

  private buildReplacementGroupPayload(groupName: string): WorkingGroup {
    const sourceGroup = this.getPendingDeletionSourceGroup();
    const periods = sourceGroup?.periods || [];
    const fallbackPeriodsCount = periods.length >= 4 ? 3 : periods.length >= 3 ? 2 : 1;
    const numberOfPeriods = sourceGroup?.numberOfPeriods || fallbackPeriodsCount || 1;

    return {
      groupName: groupName.trim(),
      year: this.selectedYear.toString(),
      numberOfPeriods,
      normalHours: this.extractHoursForType(periods, 'NORMAL'),
      summerHours: numberOfPeriods >= 2 ? this.extractHoursForType(periods, 'SUMMER') : undefined,
      ramadanHours: numberOfPeriods === 3 ? this.extractHoursForType(periods, 'RAMADAN') : undefined,
    };
  }

  private async createReplacementGroupAndGetId(): Promise<number | null> {
    const rawName = this.newReassignGroupName.trim();
    if (!rawName) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('worklog.summaries.unsaved'),
        detail: this.translate.instant('worklog.reassignNewGroupNameRequired'),
      });
      return null;
    }

    const normalizedName = rawName.toLowerCase();
    const exists = this.allWorkingGroups.some(
      (group) =>
        group.year === this.selectedYear.toString() && group.groupName?.trim().toLowerCase() === normalizedName,
    );

    if (exists) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('worklog.summaries.unsaved'),
        detail: this.translate.instant('worklog.reassignNewGroupAlreadyExists'),
      });
      return null;
    }

    this.isCreatingReassignGroup = true;
    try {
      const created = await firstValueFrom(this.workingGroupService.create(this.buildReplacementGroupPayload(rawName)));
      const createdId = created?.id ?? null;
      if (createdId == null) {
        this.messageService.add({
          severity: 'error',
          summary: this.getErrorSummary(),
          detail: this.translate.instant('worklog.reassignCreateGroupFailed'),
        });
        return null;
      }
      return createdId;
    } catch (err: any) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.resolveBackendErrorDetail(err),
      });
      return null;
    } finally {
      this.isCreatingReassignGroup = false;
    }
  }

  async confirmGroupReassignment(): Promise<void> {
    const groupId = this.groupPendingDeletionId;
    if (groupId == null) {
      return;
    }

    let targetGroupId: number | null = null;
    if (this.reassignMode === 'existing') {
      targetGroupId = this.reassignTargetGroupId;
      if (targetGroupId == null) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('worklog.summaries.unsaved'),
          detail: this.translate.instant('worklog.reassignTargetRequired'),
        });
        return;
      }
    } else {
      targetGroupId = await this.createReplacementGroupAndGetId();
      if (targetGroupId == null) {
        return;
      }
    }

    this.groupDeletionTargetByGroupId[groupId] = targetGroupId;
    this.displayGroupReassignDialog = false;
    this.groupPendingDeletionId = null;
    this.groupPendingDeletionName = '';
    this.reassignTargetGroupId = null;
    this.reassignMode = 'existing';
    this.newReassignGroupName = '';
    this.savePendingChanges();
  }

  cancelGroupReassignment(): void {
    this.displayGroupReassignDialog = false;
    this.groupPendingDeletionId = null;
    this.groupPendingDeletionName = '';
    this.reassignTargetGroupId = null;
    this.reassignMode = 'existing';
    this.newReassignGroupName = '';
    this.isCreatingReassignGroup = false;
  }

  async savePendingChanges() {
    if (!this.hasPendingChanges || this.isSavingPendingChanges) {
      return;
    }

    for (const group of this.workingGroups) {
      for (const period of group.periods || []) {
        if (!this.validateWeeklyHours(period.hoursPerWeek, this.translate.instant('worklog.hourTypes.weekly'))) {
          this.isSavingPendingChanges = false;
          return;
        }
      }
    }

    this.isSavingPendingChanges = true;
    try {
      const snapshotGroups = this.dbWorkingGroupsSnapshot.filter((group) => (group.id || 0) > 0);
      const currentExistingGroups = this.workingGroups.filter((group) => (group.id || 0) > 0);
      const currentGroupsById = new Map<number, WorkingGroup>(
        currentExistingGroups.map((group) => [group.id as number, group]),
      );

      for (const snapshotGroup of snapshotGroups) {
        const groupId = snapshotGroup.id as number;
        const currentGroup = currentGroupsById.get(groupId);

        if (!currentGroup || !(currentGroup.periods || []).length) {
          try {
            const targetGroupId = this.groupDeletionTargetByGroupId[groupId];
            await firstValueFrom(this.workingGroupService.delete(groupId, targetGroupId));
            delete this.groupDeletionTargetByGroupId[groupId];
          } catch (err: any) {
            if (this.requiresGroupReassignment(err)) {
              const opened = this.openGroupReassignDialog(groupId, snapshotGroup.groupName || '');
              if (opened) {
                return;
              }
            }
            throw err;
          }
          continue;
        }

        const snapshotPeriods = snapshotGroup.periods || [];
        const currentPeriods = currentGroup.periods || [];
        const snapshotPeriodsById = new Map<number, any>(
          snapshotPeriods.filter((period: any) => (period?.id || 0) > 0).map((period: any) => [period.id, period]),
        );

        const currentExistingPeriodIds = new Set<number>(
          currentPeriods.filter((period: any) => (period?.id || 0) > 0).map((period: any) => period.id),
        );

        for (const snapshotPeriod of snapshotPeriods) {
          const snapshotPeriodId = snapshotPeriod?.id as number;
          if ((snapshotPeriodId || 0) <= 0) {
            continue;
          }
          if (!currentExistingPeriodIds.has(snapshotPeriodId)) {
            await firstValueFrom(this.workingGroupService.deletePeriod(snapshotPeriodId));
          }
        }

        for (const currentPeriod of currentPeriods) {
          const periodId = currentPeriod?.id as number;
          if ((periodId || 0) <= 0) {
            continue;
          }

          const snapshotPeriod = snapshotPeriodsById.get(periodId);
          if (!snapshotPeriod) {
            continue;
          }

          if (this.periodHasChanges(snapshotPeriod, currentPeriod)) {
            const payload = this.buildPeriodPayload(currentPeriod);
            await firstValueFrom(this.workingGroupService.updatePeriod(periodId, payload));
          }
        }
      }

      const draftGroups = this.workingGroups.filter((group) => this.pendingGroupIds.has(group.id as number));
      for (const group of draftGroups) {
        const periods = group.periods || [];
        const payload: WorkingGroup = {
          groupName: group.groupName,
          year: this.selectedYear.toString(),
          numberOfPeriods: group.numberOfPeriods,
          normalHours: this.extractHoursForType(periods, 'NORMAL'),
          summerHours: group.numberOfPeriods >= 2 ? this.extractHoursForType(periods, 'SUMMER') : undefined,
          ramadanHours: group.numberOfPeriods === 3 ? this.extractHoursForType(periods, 'RAMADAN') : undefined,
        };

        await firstValueFrom(this.workingGroupService.create(payload));
      }

      const validExistingPeriodIds = new Set<number>(
        currentExistingGroups
          .flatMap((group) => (group.periods || []).map((period: any) => period.id as number))
          .filter((periodId) => (periodId || 0) > 0),
      );

      for (const [periodIdKey, schedule] of Object.entries(this.draftDailySchedules)) {
        const periodId = Number(periodIdKey);
        if ((periodId || 0) <= 0 || !validExistingPeriodIds.has(periodId)) {
          continue;
        }

        await firstValueFrom(this.dailyService.save(periodId, schedule));
      }

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('worklog.summaries.saved'),
        detail: this.translate.instant('worklog.allChangesSaved'),
      });
      this.loadData();
    } catch (err: any) {
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.resolveBackendErrorDetail(err),
      });
    } finally {
      this.isSavingPendingChanges = false;
    }
  }

  cancelPendingChanges() {
    if (!this.hasPendingChanges) {
      return;
    }

    this.workingGroups = this.cloneGroups(this.dbWorkingGroupsSnapshot);
    this.pendingGroupIds.clear();
    this.draftDailySchedules = {};
    this.groupDeletionTargetByGroupId = {};
    this.displayGroupReassignDialog = false;
    this.groupPendingDeletionId = null;
    this.groupPendingDeletionName = '';
    this.reassignTargetGroupId = null;
    this.reassignMode = 'existing';
    this.newReassignGroupName = '';
    this.isCreatingReassignGroup = false;
    this.hasPendingChanges = false;
    this.tempGroupId = -1;
    this.tempPeriodId = -1;
    this.rebuildWorklogRows();
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('worklog.summaries.cancelled'),
      detail: this.translate.instant('worklog.changesCancelled'),
    });
  }

  displayScheduleDialog = false;
  scheduleDirty = false;
  selectedPeriodId!: number;
  dailySchedule: DailyWorkingHour[] = [];
  private readonly weekdayNames = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

  recalculateDay(day: any) {
    if (day.workingHours === 0) {
      day.startTime = null;
      day.endTime = null;
      day.breakDuration = 0;
      return;
    }

    const periodType = this.getSelectedPeriodType();
    const defaultBreakDuration = periodType === 'RAMADAN' || periodType === 'SUMMER' ? 0 : 1;
    const hasManualBreakOverride = this.manualBreakOverrides.has(day);
    const currentBreakDuration = Number(day.breakDuration);

    if (hasManualBreakOverride) {
      day.breakDuration = Number.isFinite(currentBreakDuration) && currentBreakDuration >= 0 ? currentBreakDuration : 0;
    } else if (day.breakDuration === null || day.breakDuration === undefined || day.breakDuration === '') {
      day.breakDuration = defaultBreakDuration;
    } else if (periodType === 'RAMADAN') {
      day.breakDuration = 0;
    } else if (periodType === 'SUMMER') {
      day.breakDuration = 0;
    } else if (!Number.isFinite(currentBreakDuration) || currentBreakDuration < 0) {
      day.breakDuration = 1;
    } else if (currentBreakDuration === 0) {
      day.breakDuration = 1;
    }

    const normalizedStartTime = String(day.startTime ?? '').trim();
    const isEight =
      /^08:00(?::00(?:\.0+)?)?$/.test(normalizedStartTime) || /^8:00(?::00(?:\.0+)?)?$/.test(normalizedStartTime);
    const isNine =
      /^09:00(?::00(?:\.0+)?)?$/.test(normalizedStartTime) || /^9:00(?::00(?:\.0+)?)?$/.test(normalizedStartTime);

    // Default start time:
    // - NORMAL/SUMMER => 08:00
    // - RAMADAN => 09:00
    if (periodType === 'RAMADAN') {
      if (!normalizedStartTime || isEight) {
        day.startTime = '09:00';
      } else if (isNine) {
        day.startTime = '09:00';
      }
    } else {
      // For NORMAL/SUMMER, also migrate legacy 09:00 back to 08:00.
      if (!normalizedStartTime || isNine) {
        day.startTime = '08:00';
      } else if (isEight) {
        day.startTime = '08:00';
      }
    }

    const startMinutes = this.parseTimeToMinutes(day.startTime);
    const durationMinutes = Math.max(0, (Number(day.workingHours) || 0) * 60);
    const breakMinutes = Math.max(0, (Number(day.breakDuration) || 0) * 60);

    // Business rule: workingHours does NOT include the break time.
    // So endTime = startTime + workingHours + breakDuration.
    if (startMinutes == null || durationMinutes <= 0) {
      day.endTime = null;
      return;
    }

    day.endTime = this.formatMinutesToTime(startMinutes + durationMinutes + breakMinutes);
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) {
      return null;
    }
    const match = String(value)
      .trim()
      .match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    return hours * 60 + minutes;
  }

  private formatMinutesToTime(totalMinutes: number): string {
    const normalized = ((Math.round(totalMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  private getSelectedPeriodType(): string | null {
    const period = this.findPeriodById(this.selectedPeriodId);
    return period?.type ?? null;
  }

  private isDraftId(id: number | null | undefined): boolean {
    return (id ?? 0) < 0;
  }

  private cloneSchedule(days: DailyWorkingHour[]): DailyWorkingHour[] {
    return days.map((day) => ({ ...day }));
  }

  private buildDefaultDraftSchedule(hoursPerWeek: number, periodType: string | null): DailyWorkingHour[] {
    const weekdayTargets = this.getWeekdayTargetHours(hoursPerWeek);
    const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
    const weekend = ['SATURDAY', 'SUNDAY'];
    const defaultStartTime = periodType === 'RAMADAN' ? '09:00' : '08:00';
    const defaultEndTime = periodType === 'RAMADAN' ? '16:00' : '';
    const defaultBreakDuration = periodType === 'RAMADAN' || periodType === 'SUMMER' ? 0 : 1;

    const schedule: DailyWorkingHour[] = weekdays.map((day, index) => ({
      day,
      workingHours: weekdayTargets[index] ?? 0,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      breakDuration: defaultBreakDuration,
      notes: '',
    }));

    weekend.forEach((day) => {
      schedule.push({
        day,
        workingHours: 0,
        startTime: '',
        endTime: '',
        breakDuration: 0,
        notes: '',
      });
    });

    schedule.forEach((day) => this.recalculateDay(day));
    return schedule;
  }

  private getOrCreateDraftSchedule(period: any): DailyWorkingHour[] {
    const periodId = period?.id;
    if (this.isDraftId(periodId) && this.draftDailySchedules[periodId]) {
      return this.cloneSchedule(this.draftDailySchedules[periodId]);
    }

    const created = this.buildDefaultDraftSchedule(Number(period?.hoursPerWeek) || 0, period?.type ?? null);
    if (this.isDraftId(periodId)) {
      this.draftDailySchedules[periodId] = this.cloneSchedule(created);
    }
    return created;
  }

  loadSchedule(periodId: number) {
    if (this.draftDailySchedules[periodId]) {
      this.dailySchedule = this.cloneSchedule(this.draftDailySchedules[periodId]);
      this.applyExpectedWeekdayDistributionIfNeeded(periodId);
      this.calculateTotal();
      return;
    }

    if (this.isDraftId(periodId)) {
      const period = this.findPeriodById(periodId);
      if (!period) {
        this.dailySchedule = [];
        return;
      }

      this.dailySchedule = this.getOrCreateDraftSchedule(period);
      this.applyExpectedWeekdayDistributionIfNeeded(periodId);
      this.calculateTotal();
      return;
    }

    this.dailyService.getByPeriod(periodId).subscribe({
      next: (data) => {
        if (!data || data.length === 0) {
          this.dailyService.generate(periodId).subscribe((gen) => {
            this.dailySchedule = gen;
            this.dailySchedule.forEach((day) => {
              this.recalculateDay(day);
            });
            this.applyExpectedWeekdayDistributionIfNeeded(periodId);
            this.calculateTotal();
          });
        } else {
          this.dailySchedule = data;
          this.dailySchedule.forEach((day) => {
            this.recalculateDay(day);
          });
          this.applyExpectedWeekdayDistributionIfNeeded(periodId);
          this.calculateTotal();
        }
      },
    });
  }

  private findPeriodById(periodId: number): any | undefined {
    for (const group of this.workingGroups) {
      const period = group.periods?.find((p: any) => p.id === periodId);
      if (period) {
        return period;
      }
    }
    return undefined;
  }

  private getWeekdayRowsInOrder(): any[] {
    return this.weekdayNames
      .map((name) => this.dailySchedule.find((row: any) => row.day === name))
      .filter((row): row is any => !!row);
  }

  private getWeekdayTargetHours(hours: number): number[] {
    if (!Number.isFinite(hours) || hours <= 0) {
      return [];
    }

    // Standard workweek: Mon-Thu 9h, Fri 7h
    if (hours === 43) {
      return [9, 9, 9, 9, 7];
    }

    const perDay = hours / 5;
    const roundedOneDecimal = Math.round(perDay * 10) / 10;
    const isOneDecimalExact = Math.abs(roundedOneDecimal * 5 - hours) < 0.0001;
    if (isOneDecimalExact) {
      return [roundedOneDecimal, roundedOneDecimal, roundedOneDecimal, roundedOneDecimal, roundedOneDecimal];
    }

    const halfHoursTotal = Math.round(hours * 2);
    const base = Math.floor(halfHoursTotal / 5);
    let remainder = halfHoursTotal - base * 5;
    const parts = [base, base, base, base, base];
    let idx = 0;
    while (remainder > 0 && idx < parts.length) {
      parts[idx] += 1;
      remainder -= 1;
      idx += 1;
    }

    return parts.map((p) => p / 2);
  }

  private applyExpectedWeekdayDistributionIfNeeded(periodId: number): void {
    const period = this.findPeriodById(periodId);
    const targetHours = this.getWeekdayTargetHours(Number(period?.hoursPerWeek));
    if (targetHours.length !== 5) {
      return;
    }

    const weekdayRows = this.getWeekdayRowsInOrder();
    if (weekdayRows.length !== 5) {
      return;
    }

    const currentHours = weekdayRows.map((row) => Number(row.workingHours) || 0);
    const expectedEvenValue = Number(period.hoursPerWeek) / 5;
    const allEqual = currentHours.every((value) => Math.abs(value - currentHours[0]) < 0.0001);
    const isLegacyEvenDistribution =
      allEqual && currentHours.every((value) => Math.abs(value - expectedEvenValue) < 0.0001);

    if (!isLegacyEvenDistribution) {
      return;
    }

    weekdayRows.forEach((row, index) => {
      row.workingHours = targetHours[index];
      this.recalculateDay(row);
    });
  }

  openSchedule(period: any, group: any) {
    this.selectedGroupId = group.id;

    this.periodOptions =
      group.periods?.map((p: any) => ({
        label: p.type,
        value: p.id,
      })) || [];

    this.selectedPeriodId = period.id;

    this.scheduleDirty = false;
    this.displayScheduleDialog = true;

    this.yearPartitions = [];

    const sortedPeriods = [...group.periods].sort(
      (a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );

    sortedPeriods.forEach((p: any) => {
      if (this.draftDailySchedules[p.id]) {
        this.addPartitionRow(p, this.cloneSchedule(this.draftDailySchedules[p.id]));
        return;
      }

      if (this.isDraftId(p.id)) {
        this.addPartitionRow(p, this.getOrCreateDraftSchedule(p));
        return;
      }

      this.dailyService.getByPeriod(p.id).subscribe((days) => {
        if (!days || days.length === 0) {
          this.dailyService.generate(p.id).subscribe((genDays) => {
            this.addPartitionRow(p, genDays);
          });
        } else {
          this.addPartitionRow(p, days);
        }
      });
    });
    this.loadSchedule(period.id);
  }
  addPartitionRow(period: any, days: any[]) {
    const partialFromHours = this.buildPartialFromHours(Number(period?.hoursPerWeek));
    const partialFromSchedule = days
      .filter(
        (d) =>
          d.day === 'MONDAY' ||
          d.day === 'TUESDAY' ||
          d.day === 'WEDNESDAY' ||
          d.day === 'THURSDAY' ||
          d.day === 'FRIDAY',
      )
      .map((d) => this.formatHourValue(Number(d.workingHours) || 0))
      .join('-');
    const partial = partialFromHours !== '-' ? partialFromHours : partialFromSchedule;

    this.yearPartitions.push({
      startDate: period.startDate,
      endDate: period.endDate,
      partial: partial,
    });

    this.yearPartitions.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }

  buildPartialFromHours(hours: number): string {
    const values = this.getWeekdayTargetHours(hours);
    if (!values.length) {
      return '-';
    }
    return values.map((v) => this.formatHourValue(v)).join('-');
  }
  onScheduleDialogHide(): void {
    this.displayScheduleDialog = false;
  }

  cancelSchedule(): void {
    this.displayScheduleDialog = false;
  }

  saveSchedule() {
    for (const day of this.dailySchedule) {
      const h = Number(day.workingHours) || 0;
      if (h < 0) {
        this.messageService.add({
          severity: 'error',
          summary: this.getErrorSummary(),
          detail: this.translate.instant('worklog.dailyHoursNegative', { day: day.day }),
        });
        return;
      }
      if (h > 24) {
        this.messageService.add({
          severity: 'error',
          summary: this.getErrorSummary(),
          detail: this.translate.instant('worklog.dailyHoursExceedMax', { day: day.day, max: 24 }),
        });
        return;
      }
    }
    this.scheduleDirty = false;
    this.draftDailySchedules[this.selectedPeriodId] = this.cloneSchedule(this.dailySchedule);
    this.displayScheduleDialog = false;
    this.hasPendingChanges = true;
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('worklog.summaries.success'),
      detail: this.translate.instant('worklog.scheduleUpdatedLocal'),
    });
  }

  totalWeekHours: number = 0;
  onHoursChange(day: any) {
    this.scheduleDirty = true;
    const enteredHours = Number(day.workingHours) || 0;
    if (enteredHours > this.MAX_HOURS_PER_DAY) {
      day.workingHours = this.MAX_HOURS_PER_DAY;
      this.messageService.add({
        severity: 'error',
        summary: this.getErrorSummary(),
        detail: this.translate.instant('worklog.hoursExceedDailyMax')
      });
    }
    this.recalculateDay(day);
    this.calculateTotal();
    this.updatePartialDistribution();
  }

  onBreakChange(day: any) {
    this.scheduleDirty = true;
    this.manualBreakOverrides.add(day);
    this.syncWorkingHoursFromTimeFields(day);
    this.calculateTotal();
    this.updatePartialDistribution();
  }

  calculateTotal() {
    this.totalWeekHours = this.dailySchedule
      .reduce((sum: number, d: any) =>
        sum + (Number(d.workingHours) || 0), 0);

    this.updatePartialDistribution();
  }

  onStartTimeChange(day: any) {
    this.scheduleDirty = true;
    this.syncWorkingHoursFromTimeFields(day);
    this.calculateTotal();
    this.updatePartialDistribution();
  }

  onEndTimeChange(day: any) {
    this.scheduleDirty = true;
    this.syncWorkingHoursFromTimeFields(day);
    this.calculateTotal();
    this.updatePartialDistribution();
  }

  selectedGroupId!: number;
  groupOptions: any[] = [];
  periodOptions: any[] = [];
  onGroupChange() {
    const group = this.workingGroups.find((g) => g.id === this.selectedGroupId);

    if (!group || !group.periods) {
      this.periodOptions = [];
      this.selectedPeriodId = null as any;
      return;
    }

    this.periodOptions = group.periods.map((p) => ({
      label: p.type,
      value: p.id,
    }));

    this.selectedPeriodId = this.periodOptions[0]?.value;

    if (this.selectedPeriodId) {
      this.loadSchedule(this.selectedPeriodId);
    }
  }

  onPeriodChange() {
    if (!this.selectedPeriodId) return;

    const group = this.workingGroups.find((g) => g.id === this.selectedGroupId);

    const period = group?.periods?.find((p) => p.id === this.selectedPeriodId);

    if (period) {
      this.selectedPeriodStartDate = period.startDate;
      this.selectedPeriodEndDate = period.endDate;
    }

    this.loadSchedule(this.selectedPeriodId);
  }

  partialDistribution: string = '';
  selectedPeriodStartDate!: string;
  selectedPeriodEndDate!: string;

  updatePartialDistribution() {
    const weekdays = this.dailySchedule.filter(
      (d) =>
        d.day === 'MONDAY' ||
        d.day === 'TUESDAY' ||
        d.day === 'WEDNESDAY' ||
        d.day === 'THURSDAY' ||
        d.day === 'FRIDAY',
    );

    this.partialDistribution = weekdays.map((d) => this.formatHourValue(Number(d.workingHours) || 0)).join('-');
  }

  private formatHourValue(value: number): string {
    return Number.isInteger(value) ? value.toString() : value.toFixed(1).replace(/\.0$/, '');
  }

  private syncWorkingHoursFromTimeFields(day: any): void {
    const startMinutes = this.parseTimeToMinutes(day.startTime);
    const endMinutes = this.parseTimeToMinutes(day.endTime);
    if (startMinutes == null || endMinutes == null) {
      return;
    }

    const breakMinutes = this.resolveBreakMinutes(day);
    const rawHours = (endMinutes - startMinutes - breakMinutes) / 60;
    day.workingHours = Math.max(0, this.roundToTwoDecimals(rawHours));
  }

  private resolveBreakMinutes(day: any): number {
    const periodType = this.getSelectedPeriodType();
    const defaultBreakDuration = periodType === 'RAMADAN' || periodType === 'SUMMER' ? 0 : 1;
    const currentBreakDuration = Number(day.breakDuration);

    if (day.breakDuration === null || day.breakDuration === undefined || day.breakDuration === '') {
      day.breakDuration = defaultBreakDuration;
      return defaultBreakDuration * 60;
    }

    if (Number.isFinite(currentBreakDuration) && currentBreakDuration >= 0) {
      day.breakDuration = currentBreakDuration;
      return currentBreakDuration * 60;
    }

    day.breakDuration = defaultBreakDuration;
    return defaultBreakDuration * 60;
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
